import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { CreditCard, ArrowRight, Star, Users, Loader2, UserMinus } from 'lucide-react';
import { useState, useEffect } from 'react';
import { teamService } from '../../services';
import { useAuth } from '@/providers';

export function BillingSettings() {
    const navigate = useNavigate();
    const { user, getUserPrimaryTeam, isTeamOwner } = useAuth();
    const [userTeam, setUserTeam] = useState<any>(null);
    const [isOwner, setIsOwner] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTeam = async () => {
            try {
                const teamData = await getUserPrimaryTeam();
                setUserTeam(teamData);
                if (teamData) {
                    setIsOwner(isTeamOwner(teamData.team.id));
                }
            } catch (error) {
                console.error('Failed to load team data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchTeam();
    }, [getUserPrimaryTeam, isTeamOwner]);

    const getTierIcon = (tier: string) => {
        switch (tier) {
            case 'starter':
                return <Star className="h-4 w-4" />;
            case 'team':
                return <Users className="h-4 w-4" />;
            default:
                return <CreditCard className="h-4 w-4" />;
        }
    };

    const getTierColor = (tier: string): "default" | "destructive" | "outline" | "secondary" => {
        switch (tier) {
            case 'free':
                return 'secondary';
            case 'starter':
                return 'default';
            case 'team':
                return 'default';
            default:
                return 'secondary';
        }
    };

    const getTierName = (tier: string) => {
        switch (tier) {
            case 'free':
                return 'Free';
            case 'starter':
                return 'Starter';
            case 'team':
                return 'Team';
            default:
                return 'Free';
        }
    };

    const getTierDescription = (tier: string) => {
        switch (tier) {
            case 'free':
                return 'Free tier with basic features';
            case 'starter':
                return 'Starter plan with enhanced features';
            case 'team':
                return 'Team plan with collaboration features';
            default:
                return 'Free tier with basic features';
        }
    };

    const canUpgrade = (tier: string) => {
        return tier !== 'team';
    };

    const handleLeaveTeam = async () => {
        if (!userTeam) return;

        try {
            await teamService.leaveTeam(userTeam.team.id);
        } catch (error) {
            console.error('Failed to leave team:', error);
        }
        window.location.reload();
    };

    // If user is part of a team but not the owner, show restricted view
    const isTeamMemberNotOwner = userTeam && !isOwner;

    if (loading) {
        return (
            <Card className="p-6">
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading subscription...</span>
                </div>
            </Card>
        );
    }

    const currentTier = user?.billingInfo?.subscriptionTier || 'free';

    return (
        <Card className="p-6">
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-medium">Billing & Subscription</h3>
                    <p className="text-sm text-muted-foreground">
                        Manage your subscription and billing information.
                    </p>
                </div>

                <Separator />

                <div className="space-y-4">
                    {!isTeamMemberNotOwner && <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-3">
                            {getTierIcon(currentTier)}
                            <div>
                                <h4 className="font-medium">Current Plan</h4>
                                <p className="text-sm text-muted-foreground">{getTierDescription(currentTier)}</p>
                            </div>
                        </div>
                        <Badge variant={getTierColor(currentTier)}>
                            {getTierName(currentTier)}
                        </Badge>
                    </div>}

                    {/* Show team member view if user is part of a team but not the owner */}
                    {isTeamMemberNotOwner ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-800 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-medium text-orange-900 dark:text-orange-100">Team Member</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300">
                                            You're a member of "{userTeam.team.name}". Only team owners can manage billing.
                                        </p>
                                    </div>
                                    <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                                </div>
                            </div>

                            <div className="p-4 border rounded-lg">
                                <h5 className="font-medium mb-2">Leave Team</h5>
                                <p className="text-sm text-muted-foreground mb-3">
                                    If you leave this team, you'll lose access to shared resources and will need to create your own subscription.
                                </p>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={handleLeaveTeam}
                                    className="flex items-center gap-2"
                                >
                                    <UserMinus className="h-4 w-4" />
                                    Leave Team
                                </Button>
                            </div>
                        </div>
                    ) : (
                        // Show normal billing view for individual users or team owners
                        <div className="space-y-4">
                            {canUpgrade(currentTier) ? (
                                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg">
                                    <div>
                                        <h4 className="font-medium text-blue-900 dark:text-blue-100">Upgrade Your Plan</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300">
                                            Access advanced features and premium subscription options
                                        </p>
                                    </div>
                                    <Button
                                        onClick={() => navigate('/app/billing')}
                                        className="flex items-center gap-2"
                                    >
                                        <CreditCard className="h-4 w-4" />
                                        Manage Billing
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 rounded-lg">
                                    <div>
                                        <h4 className="font-medium text-green-900 dark:text-green-100">Team Plan Active</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300">
                                            You're on the highest tier with collaboration features
                                        </p>
                                    </div>
                                    <Button
                                        onClick={() => navigate('/app/billing')}
                                        variant="outline"
                                        className="flex items-center gap-2"
                                    >
                                        <CreditCard className="h-4 w-4" />
                                        Manage Billing
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}
